import { useEffect, useRef, useState } from "react";
import {
  Modal, ModalOverlay, ModalContent, ModalBody,
  Flex, Heading, Button, Box, Text,
} from "@chakra-ui/react";
import { FaCompress, FaFileExcel, FaSave } from "react-icons/fa";
import { crm } from "../../../theme";
import { columns as allColumns } from "./columns";
import { ReportFilters, DateRangeFilter } from "./newReportModal";
import { TEXT_FILTER_FIELDS, DATE_FILTER_FIELDS } from "./filterFields";
import { DateTime } from "luxon";
import TableWithPagination from "../../../components/TableWithPagination";
import { Tag } from "../../../models/Filter";
import { exportProcessesExcel } from "../../../services/processService";

interface ReportPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedColumns: string[];
  filters: ReportFilters;
  name: string,
  getProcesses: (params: any) => Promise<any>;
  currentCompany: any;
  currentEmulation: any;
  onSave: () => void;
  hideSave?: boolean;
}

const formatDate = (iso: string) =>
  DateTime.fromISO(iso).isValid
    ? DateTime.fromISO(iso).toFormat("dd/LL/yyyy")
    : iso;

const TEXT_KEY_TO_LABEL = Object.fromEntries(TEXT_FILTER_FIELDS.map((f) => [f.key, f.label]));
const DATE_KEY_TO_LABEL = Object.fromEntries(DATE_FILTER_FIELDS.map((f) => [f.key, f.label]));

// Mapeia os filtros do modal para os parâmetros exatos do getProcesses
const buildQueryParams = (
  filters: ReportFilters,
  currentCompany: any,
  currentEmulation: any,
  pageSize: number,
  pageNumber: number,
  sortField?: string,
  sortDirection?: string,
) => {
  const t = filters.text;
  const d = filters.dates;

  // Helper: retorna valor ou undefined se vazio
  const str = (v?: string | null) => v || undefined;
  const date = (v?: string | null) => v || undefined;

  return {
    pageSize,
    pageNumber,
    getAll: true,
    type: currentCompany,
    companyId: currentEmulation,
    sortField,
    sortDirection,

    // ── Campos de texto ─────────────────────────────────
    cvfRef:          str(t["CVFRef"]),
    multiClientId:   str(t["Client"]),
    exporter:        str(t["Exporter"]),
    multiStatus:     str(t["Status"]),
    responsible:     str(t["Responsible"]),
    channel:         str(t["Channel"]),
    di:              str(t["Di"]),
    li:              str(t["Li"]),
    BlAwbCrt:        str(t["blAwb"]),
    containerNumber: str(t["ContainerNumber"]),
    ncm:             str(t["Ncm"]),
    incoterm:        str(t["Incoterm"]),

    // ── Campos de texto adicionais ───────────────────────
    clientRef:            str(t["ClientRef"]),
    refName:              str(t["RefName"]),
    dispatcher:            str(t["Dispatcher"]),
    importMode:            str(t["ImportMode"]),
    totalInvoice:          str(t["TotalInvoice"]),
    internationalFreight:  str(t["InternationalFreight"]),
    ceMerchant:            str(t["CeMerchant"]),
    paymentCondition:      str(t["PaymentCondition"]),
    invoice:               str(t["Invoice"]),
    containerQuantity:     str(t["ContainerQuantity"]),
    vessel:                str(t["Vessel"]),
    carrier:               str(t["Carrier"]),
    countryOfOrigin:       str(t["CountryOfOrigin"]),
    dischargePort:         str(t["DischargePort"]),
    customsAgent:          str(t["CustomsAgent"]),
    demurrage:             str(t["Demurrage"]),
    paymentStatus:         str(t["PaymentStatus"]),
    currency:              str(t["Currency"]),
    freeTime:              str(t["FreeTime"]),
    internationalQuote:    str(t["InternationalQuote"]),
    roadQuote:             str(t["RoadQuote"]),
    insurance:             str(t["Insurance"]),
    icms:                  str(t["Icms"]),
    cashAmount:            str(t["CashAmount"]),
    documents:             str(t["Documents"]),
    catalogPu:             str(t["CatalogPu"]),
    liStatus:              str(t["LiStatus"]),
    segment:               str(t["Segment"]),
    taxaCambio1:           str(t["TaxaCambio1"]),
    taxaCambio2:           str(t["TaxaCambio2"]),
    taxaCambio3:           str(t["TaxaCambio3"]),
    valorPagamento1:       str(t["ValorPagamento1"]),
    valorPagamento2:       str(t["ValorPagamento2"]),
    valorPagamento3:       str(t["ValorPagamento3"]),
    contratoCambio1:       str(t["ContratoCambio1"]),
    contratoCambio2:       str(t["ContratoCambio2"]),

    // ── Data Abertura ────────────────────────────────────
    dateType:     "openingDate",
    initialDate:  date(d["OpeningDate"]?.from),
    finalDate:    date(d["OpeningDate"]?.to),

    // ── Data Chegada ─────────────────────────────────────
    arrivalInitialDate: date(d["ArrivalDate"]?.from),
    arrivalFinalDate:   date(d["ArrivalDate"]?.to),

    // ── Data Embarque ────────────────────────────────────
    shippingInitialDate: date(d["ShippingDate"]?.from),
    shippingFinalDate:   date(d["ShippingDate"]?.to),

    // ── Data Registro ────────────────────────────────────
    registrationInitialDate: date(d["RegistrationDate"]?.from),
    registrationFinalDate:   date(d["RegistrationDate"]?.to),

    // ── Data Desembaraço ─────────────────────────────────
    clearanceInitialDate: date(d["CustomsClearanceDate"]?.from),
    clearanceFinalDate:   date(d["CustomsClearanceDate"]?.to),

    // ── Data Emissão NF ──────────────────────────────────
    nfIssuedInitialDate: date(d["NfIssued"]?.from),
    nfIssuedFinalDate:   date(d["NfIssued"]?.to),

    // ── Parcelas (câmbio / pagamento) ─────────────────────
    dataCambio1Initial: date(d["DataCambio1"]?.from),
    dataCambio1Final:   date(d["DataCambio1"]?.to),
    dataCambio2Initial: date(d["DataCambio2"]?.from),
    dataCambio2Final:   date(d["DataCambio2"]?.to),
    dataCambio3Initial: date(d["DataCambio3"]?.from),
    dataCambio3Final:   date(d["DataCambio3"]?.to),
    dataPagamento1Initial: date(d["DataPagamento1"]?.from),
    dataPagamento1Final:   date(d["DataPagamento1"]?.to),
    dataPagamento2Initial: date(d["DataPagamento2"]?.from),
    dataPagamento2Final:   date(d["DataPagamento2"]?.to),
    dataPagamento3Initial: date(d["DataPagamento3"]?.from),
    dataPagamento3Final:   date(d["DataPagamento3"]?.to),
  };
};

const ReportPreviewModal = ({name,
  isOpen, onClose, selectedColumns, filters,
  getProcesses, currentCompany, currentEmulation, onSave, hideSave,
}: ReportPreviewModalProps) => {
  const [page, setPage] = useState<any[]>([]);
  const [outerTotal, setOuterTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [tags] = useState<Tag[]>([]);
  const [exporting, setExporting] = useState(false);
  const hasFetched = useRef(false);

  const handleExportExcel = async () => {
    setExporting(true);
    try {
      const params = buildQueryParams(
        filters, currentCompany, currentEmulation, 0, 0,
      );
      const file = await exportProcessesExcel({
        ...params,
        getAll: true,
        columns: selectedColumns,
      } as any);
      if (!file) return;
      const linkSource = `data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,${file}`;
      const downloadLink = document.createElement("a");
      downloadLink.href = linkSource;
      downloadLink.download = `${name || "Relatório"} ${DateTime.now().toFormat("dd-LL-yyyy")}.xlsx`;
      downloadLink.click();
    } finally {
      setExporting(false);
    }
  };

  const fetchProcesses = async (
    ps: number,
    pn: number,
    sortField?: string,
    sortDirection?: string,
  ) => {
    setLoading(true);
    try {
      const params = buildQueryParams(
        filters, currentCompany, currentEmulation, ps, pn, sortField, sortDirection,
      );
      const response = await getProcesses(params);      
      setPage(response?.data ?? []);
      setOuterTotal(response?.totalCount ?? response?.data?.length ?? 0);
      return response ?? { data: [], totalCount: 0 };
    } catch (err) {
      console.error("Erro ao buscar processos:", err);
      setPage([]);
      setOuterTotal(0);
      return { data: [], totalCount: 0 };
    } finally {
      setLoading(false);
    }
  };

  const get = async (
    localPageSize?: number,
    localPageNumber?: number,
    sortField?: string,
    sortDirection?: string,
  ) =>
    fetchProcesses(
      localPageSize ?? pageSize,
      localPageNumber ?? pageIndex,
      sortField,
      sortDirection,
    );

  useEffect(() => {
    if (isOpen && !hasFetched.current) {
      hasFetched.current = true;
      setPage([]);
      setPageIndex(0);
      setOuterTotal(0);
      fetchProcesses(pageSize, 0);
    }
    if (!isOpen) {
      hasFetched.current = false;
    }
  }, [isOpen]);

  const visibleColumns = allColumns.filter((c) => selectedColumns.includes(c.label));

  const activeTextFilters = Object.entries(filters.text).filter(([, v]) => v.length > 0);
  const activeDateFilters = (Object.entries(filters.dates) as [string, DateRangeFilter][])  
    .filter(([, v]) => v.from.length > 0 || v.to.length > 0);
  const hasActiveFilters = activeTextFilters.length > 0 || activeDateFilters.length > 0;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="full">
      <ModalOverlay backdropFilter="blur(2px)" bg="blackAlpha.600" />
      <ModalContent margin={0} borderRadius={0}>
        <ModalBody p="16px" display="flex" flexDirection="column" height="100vh">

          {/* Header */}
          <Flex justifyContent="space-between" alignItems="flex-start" mb="16px" gap="16px">
            <Box>
              <Heading size="md" fontWeight="normal">
                Visualização do Relatório - {name}
              </Heading>
              {hasActiveFilters && (
                <Flex flexWrap="wrap" gap="8px" mt="8px">
                  {activeTextFilters.map(([key, value]) => (
                    <Flex key={key} alignItems="center" gap="4px"
                      backgroundColor="blue.50" borderRadius="4px"
                      px="8px" py="3px" borderWidth="1px" borderColor="blue.200">
                      <Text fontSize="xs" color="blue.600" fontWeight="medium">
                        {TEXT_KEY_TO_LABEL[key] ?? key}:
                      </Text>
                      <Text fontSize="xs" color="blue.800">
                        {key === "Client"
                          ? (filters.clientLabels
                              ? filters.clientLabels.split("|").join(", ")
                              : `${value.split("|").filter(Boolean).length} cliente(s)`)
                          : value}
                      </Text>
                    </Flex>
                  ))}
                  {activeDateFilters.map(([key, filter]) => (
                    <Flex key={key} alignItems="center" gap="4px"
                      backgroundColor="purple.50" borderRadius="4px"
                      px="8px" py="3px" borderWidth="1px" borderColor="purple.200">
                      <Text fontSize="xs" color="purple.600" fontWeight="medium">
                        {DATE_KEY_TO_LABEL[key] ?? key}:
                      </Text>
                      {filter.from && (
                        <Text fontSize="xs" color="purple.800">
                          de {formatDate(filter.from)}
                        </Text>
                      )}
                      {filter.to && (
                        <Text fontSize="xs" color="purple.800">
                          até {formatDate(filter.to)}
                        </Text>
                      )}
                    </Flex>
                  ))}
                </Flex>
              )}
            </Box>

            <Flex gap="12px" flexShrink={0}>
              <Button variant="outline" borderRadius="10px" onClick={onClose}>
                <Flex alignItems="center" gap="8px">
                  <FaCompress /> Voltar
                </Flex>
              </Button>
              <Button
                variant="outline" borderRadius="10px"
                onClick={handleExportExcel} isLoading={exporting}
                leftIcon={<FaFileExcel />}
              >
                Baixar Excel
              </Button>
              {!hideSave && (
                <Button
                  backgroundColor="var(--icon-color)" color="white" borderRadius="10px"
                  _hover={{ backgroundColor: crm.brandDark }}
                  onClick={onSave} leftIcon={<FaSave />}
                >
                  Salvar relatório
                </Button>
              )}
            </Flex>
          </Flex>

          {/* Info bar */}
          <Flex mb="12px" gap="16px" alignItems="center">
            <Text fontSize="sm" color={crm.textMuted}>
              <b>{selectedColumns.length}</b> coluna{selectedColumns.length !== 1 ? "s" : ""} selecionada{selectedColumns.length !== 1 ? "s" : ""}
            </Text>
            {!loading && (
              <Text fontSize="sm" color={crm.textMuted}>
                <b>{outerTotal}</b> processo{outerTotal !== 1 ? "s" : ""} encontrado{outerTotal !== 1 ? "s" : ""}
              </Text>
            )}
          </Flex>

          {/* Table */}
          <Box flex="1" overflowY="auto" backgroundColor={crm.surface} borderRadius="12px" border={`1px solid ${crm.border}`} boxShadow={crm.shadowSm} overflow="hidden" p="16px">
            <TableWithPagination
              isProcess
              loading={loading}
              outerTotal={outerTotal}
              columns={visibleColumns}
              page={page}
              setPage={setPage}
              get={get}
              pageIndex={pageIndex}
              setPageIndex={setPageIndex}
              pageSize={pageSize}
              setPageSize={setPageSize}
              tags={tags}
              emptyMessage="Nenhum processo encontrado com os filtros selecionados"
            />
          </Box>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default ReportPreviewModal;
